import type { TOptions } from 'i18next';
import i18next from './i18next';
import type { resources } from './resources';

export { supportedLngs, namespaces } from './i18next';

type Catalogs = (typeof resources)['sv'];

/**
 * Every `'namespace:key'` the catalogs define. Derived from the resources rather than from
 * i18next's own parameter types, which carry branded selector-API variants that do not
 * survive an Extract.
 */
export type MessageKey = { [N in keyof Catalogs]: `${N & string}:${keyof Catalogs[N] & string}` }[keyof Catalogs];

/** Accepts both `'ns:key'` and the legacy `'ns.key'`, where the first dot is the boundary. */
const qualify = (key: string): string => {
  if (key.includes(':')) return key;
  const boundary = key.indexOf('.');
  return boundary === -1 ? key : `${key.slice(0, boundary)}:${key.slice(boundary + 1)}`;
};

/** For non-component modules. Components should use `useTranslation(ns)`. */
export function i18n(key: MessageKey, values?: TOptions): string {
  if (i18next.language === 'cimode') return key;
  return i18next.t(key, { ...values, defaultValue: key });
}

/**
 * For keys assembled at runtime from values TypeScript cannot enumerate — an HTTP status
 * code, a namespace picked by a flag, a concatenated field name. Deliberately named and
 * greppable rather than a scattered `as MessageKey`; i18next's missingKeyHandler warns in
 * dev when one of these does not resolve. Prefer `i18n`/`t` wherever the key is a literal.
 */
export function i18nDynamic(key: string, values?: TOptions): string {
  return i18n(qualify(key) as MessageKey, values);
}

/**
 * Translates a string only if it happens to be a key; other text passes through.
 * Deliberately untyped — the input is user data or a key held in config, not a literal.
 */
export function i18nMaybe(maybeKey: string): string {
  const key = qualify(maybeKey) as MessageKey;
  return i18next.exists(key) ? i18next.t(key) : maybeKey;
}
